import * as z from "zod/v4-mini"
import {z as legacyZod} from "zod"
import type {ExtensionT} from "#utils/content-types"
import type {ImageTilesDescriptor} from "#utils/tiled-image/tiled-image-types"

export namespace DataObjectDefinitions {
    export type ImageFormat = Exclude<ExtensionT, "zip" | "bmp">
    export type DerivedImageFormat = Exclude<ImageFormat, "exr" | "png">
    // TODO, possible to avoid duplication?
    export const DERIVED_IMAGE_FORMAT_AND_RESOLUTION_SET = [
        {format: "jpg", resolution: "original"},
        {format: "tif", resolution: "original"},
        {format: "jpg", resolution: "px2000"},
        {format: "jpg", resolution: "px1000"},
        {format: "jpg", resolution: "px500"},
    ] as const

    export const derivedImageFilenameWithFormatAndResolutionSetSchema = z.readonly(
        z.tuple([
            z.object({format: z.literal("jpg"), resolution: z.literal("original"), filename: z.nullable(z.string())}),
            z.object({format: z.literal("tif"), resolution: z.literal("original"), filename: z.nullable(z.string())}),
            z.object({format: z.literal("jpg"), resolution: z.literal("px2000"), filename: z.nullable(z.string())}),
            z.object({format: z.literal("jpg"), resolution: z.literal("px1000"), filename: z.nullable(z.string())}),
            z.object({format: z.literal("jpg"), resolution: z.literal("px500"), filename: z.nullable(z.string())}),
        ]),
    )
    export const legacyDerivedImageFilenameWithFormatAndResolutionSetSchema = legacyZod
        .tuple([
            legacyZod.object({format: legacyZod.literal("jpg"), resolution: legacyZod.literal("original"), filename: legacyZod.nullable(legacyZod.string())}),
            legacyZod.object({format: legacyZod.literal("tif"), resolution: legacyZod.literal("original"), filename: legacyZod.nullable(legacyZod.string())}),
            legacyZod.object({format: legacyZod.literal("jpg"), resolution: legacyZod.literal("px2000"), filename: legacyZod.nullable(legacyZod.string())}),
            legacyZod.object({format: legacyZod.literal("jpg"), resolution: legacyZod.literal("px1000"), filename: legacyZod.nullable(legacyZod.string())}),
            legacyZod.object({format: legacyZod.literal("jpg"), resolution: legacyZod.literal("px500"), filename: legacyZod.nullable(legacyZod.string())}),
        ])
        .readonly()

    // export type DerivedImageResolution = (typeof DERIVED_IMAGE_FORMAT_AND_RESOLUTION_SET)[number]["resolution"]
    // type AuxT<K extends ReadonlyArray<any>, E> = {[I in keyof K]: K[I] & E}
    // export type DerivedImageFilenameWithFormatAndResolutionSet = AuxT<typeof DERIVED_IMAGE_FORMAT_AND_RESOLUTION_SET, {filename: string | null}>

    export type DerivedImageFilenameWithFormatAndResolutionSet = z.infer<typeof derivedImageFilenameWithFormatAndResolutionSetSchema>
    export type LegacyDerivedImageFilenameWithFormatAndResolutionSet = legacyZod.infer<typeof legacyDerivedImageFilenameWithFormatAndResolutionSetSchema>
    export type DerivedImageResolution = DerivedImageFilenameWithFormatAndResolutionSet[number]["resolution"]
}

export type ImageFormat = DataObjectDefinitions.ImageFormat

const imageColorSpace_ = ["Gamma_2_0", "Gamma_2_2", "Linear", "Srgb", "Unknown"] as const
export const imageColorSpaceSchema = z.enum(imageColorSpace_)
export const legacyImageColorSpaceSchema = legacyZod.enum(imageColorSpace_)
export const ImageColorSpace = imageColorSpaceSchema.def.entries
export type ImageColorSpace = "Gamma_2_0" | "Gamma_2_2" | "Linear" | "Srgb" | "Unknown"

// TODO unify that a bit with ./utils/data-object.ts. Also defined in template nodes
const thumbnailResolution_ = ["original", "2000px", "1000px", "500px"] as const
export const thumbnailResolutionSchema = z.enum(thumbnailResolution_)
export const ThumbnailResolution = thumbnailResolutionSchema.def.entries
export type ThumbnailResolution = z.infer<typeof thumbnailResolutionSchema>

const mediaType_ = [
    "application/zip",
    "image/webp",
    "image/jpeg",
    "image/tiff",
    "image/png",
    "image/bmp",
    "image/x-exr",
    "image/exr",
    "image/vnd.radiance",
    "application/octet-stream",
    "application/draco",
    "application/ply",
    "application/obj",
] as const
export const mediaTypeSchema = z.enum(mediaType_)
export const legacyMediaTypeSchema = legacyZod.enum(mediaType_)
export const MediaType = mediaTypeSchema.def.entries
export type MediaType = z.infer<typeof mediaTypeSchema>

export enum DownloadFileType {
    Exr = "exr",
    Jpg = "jpg",
    Png = "png",
    Tiff = "tiff",
}

export function mapImageFormatToDownloadFileType(format: ImageFormat): DownloadFileType {
    switch (format) {
        case "exr":
            return DownloadFileType.Exr
        case "jpg":
            return DownloadFileType.Jpg
        case "png":
            return DownloadFileType.Png
        case "tif":
            return DownloadFileType.Tiff
        default:
            throw Error(`Unsupported format ${format}`)
    }
}

export enum DownloadResolution {
    High = "High",
    Medium = "Medium",
    Low = "Low",
    Tiny = "Tiny",
}

export function mapImageResolutionToDownloadResolution(resolution: ThumbnailResolution): DownloadResolution {
    switch (resolution) {
        case "original":
            return DownloadResolution.High
        case "2000px":
            return DownloadResolution.Medium
        case "1000px":
            return DownloadResolution.Low
        case "500px":
            return DownloadResolution.Tiny
        default:
            throw Error(`Unsupported resolution ${resolution}`)
    }
}

export function mapDownloadResolutionToImageResolution(resolution: DownloadResolution): ThumbnailResolution {
    switch (resolution) {
        case DownloadResolution.High:
            return "original"
        case DownloadResolution.Medium:
            return "2000px"
        case DownloadResolution.Low:
            return "1000px"
        case DownloadResolution.Tiny:
            return "500px"
        default:
            throw Error(`Unsupported resolution ${resolution}`)
    }
}

export const imageTilesDescriptorSchema = z.object({
    imageWidth: z.number(),
    imageHeight: z.number(),
    tileWidth: z.number(),
    tileHeight: z.number(),
    tileMargin: z.number(),
    tileMipLevels: z.number(),
    tileMediaType: mediaTypeSchema,
    tileDownloadUrlPrefix: z.string(),
})

export const dataObjectSchema = z.object({
    id: z.string(),
    legacyId: z.number(),
    originalFileName: z.string(),
    imageColorSpace: z.nullish(imageColorSpaceSchema),
    mediaType: mediaTypeSchema,
    width: z.pipe(
        z.nullish(z.number()),
        z.transform((val) => (val === null ? undefined : val)),
    ),
    height: z.pipe(
        z.nullish(z.number()),
        z.transform((val) => (val === null ? undefined : val)),
    ),
    size: z.nullish(z.number()),
    downloadUrl: z.string(),
})

export type DataObject = {
    id: string
    legacyId: number
    originalFileName: string
    imageColorSpace?: ImageColorSpace | null
    mediaType: MediaType
    width?: number | null
    height?: number | null
    size?: number | null
    downloadUrl: string
    imageTilesDescriptor?: ImageTilesDescriptor
}

export type DataObjectWithRelated = DataObject & {
    related?: DataObject[]
}
